type ThemeTokenValue = string | number | null | undefined;
type ThemeVariableName = `--${string}`;
type ThemeStyle = {
  setProperty: (name: string, value: string) => void;
  removeProperty: (name: string) => void;
};
type ThemeTarget = {
  style?: ThemeStyle;
  querySelector?: (selector: string) => { style?: ThemeStyle } | null;
};

export const agenticThemeColorVariables = {
  primary: "--bfrs-color-primary",
  primaryBright: "--bfrs-color-primary-bright",
  primaryHover: "--bfrs-color-primary-hover",
  primaryActive: "--bfrs-color-primary-active",
  primarySoft: "--bfrs-color-primary-soft",
  primaryBorder: "--bfrs-color-primary-border",
  primaryContrast: "--bfrs-color-primary-contrast",
  accent: "--bfrs-color-accent",
  accentSoft: "--bfrs-color-accent-soft",
  surfaceApp: "--bfrs-color-surface-app",
  surfaceShell: "--bfrs-color-surface-shell",
  surfaceCard: "--bfrs-color-surface-card",
  surfaceMuted: "--bfrs-color-surface-muted",
  surfaceRaised: "--bfrs-color-surface-raised",
  textPrimary: "--bfrs-color-text-primary",
  textSecondary: "--bfrs-color-text-secondary",
  textMuted: "--bfrs-color-text-muted",
  textDisabled: "--bfrs-color-text-disabled",
  textInverse: "--bfrs-color-text-inverse",
  border: "--bfrs-color-border-default",
  borderAlias: "--bfrs-color-border",
  borderLight: "--bfrs-color-border-light",
  borderStrong: "--bfrs-color-border-strong",
  borderFocus: "--bfrs-color-border-focus",
  formControlBorder: "--bfrs-color-form-control-border",
  borderCard: "--bfrs-color-border-card",
  brandWhatsapp: "--bfrs-color-brand-whatsapp",
  brandInstagram: "--bfrs-color-brand-instagram",
  brandFacebook: "--bfrs-color-brand-facebook",
  success: "--bfrs-color-success",
  successSoft: "--bfrs-color-success-soft",
  successContrast: "--bfrs-color-success-contrast",
  danger: "--bfrs-color-danger",
  dangerSoft: "--bfrs-color-danger-soft",
  dangerContrast: "--bfrs-color-danger-contrast",
  warning: "--bfrs-color-warning",
  warningSoft: "--bfrs-color-warning-soft",
  warningContrast: "--bfrs-color-warning-contrast",
  info: "--bfrs-color-info",
  infoSoft: "--bfrs-color-info-soft",
  infoContrast: "--bfrs-color-info-contrast",
  agentOrders: "--bfrs-color-agent-orders",
  agentNdr: "--bfrs-color-agent-ndr",
  agentRto: "--bfrs-color-agent-rto",
  agentTracking: "--bfrs-color-agent-tracking",
  agentInsights: "--bfrs-color-agent-insights",
  dclPrimary: "--bfrs-dcl-color-primary",
  dclPrimaryLight: "--bfrs-dcl-color-primary-light",
  dclPrimaryBorder: "--bfrs-dcl-color-primary-border",
  dclAccent: "--bfrs-dcl-color-accent",
  dclAccentLight: "--bfrs-dcl-color-accent-light",
  dclBackground: "--bfrs-dcl-color-background",
  dclShellBackground: "--bfrs-dcl-color-shell-background",
  dclSurface: "--bfrs-dcl-color-surface",
  dclTableAltRow: "--bfrs-dcl-color-table-alt-row",
  dclBorder: "--bfrs-dcl-color-border",
  dclBorderLight: "--bfrs-dcl-color-border-light",
  dclBorderStrong: "--bfrs-dcl-color-border-strong",
  dclNeutralStroke2: "--bfrs-dcl-color-neutral-stroke-2",
  dclText: "--bfrs-dcl-color-text",
  dclTextMuted: "--bfrs-dcl-color-text-muted",
  dclTextLight: "--bfrs-dcl-color-text-light",
  dclTextInverse: "--bfrs-dcl-color-text-inverse",
  dclClickHover: "--bfrs-dcl-color-click-hover",
  statusDelivered: "--bfrs-dcl-color-status-delivered",
  statusDeliveredBg: "--bfrs-dcl-color-status-delivered-bg",
  statusInTransit: "--bfrs-dcl-color-status-in-transit",
  statusInTransitBg: "--bfrs-dcl-color-status-in-transit-bg",
  statusPending: "--bfrs-dcl-color-status-pending",
  statusPendingBg: "--bfrs-dcl-color-status-pending-bg",
  statusFailed: "--bfrs-dcl-color-status-failed",
  statusFailedBg: "--bfrs-dcl-color-status-failed-bg",
  businessCardText: "--bfrs-dcl-color-business-card-text",
  businessCardSubtitle: "--bfrs-dcl-color-business-card-subtitle",
  businessCardMuted: "--bfrs-dcl-color-business-card-muted",
  chatAssistantEyebrow: "--bfrs-dcl-color-chat-assistant-eyebrow",
  chatAssistantTitle: "--bfrs-dcl-color-chat-assistant-title",
  chatAssistantBody: "--bfrs-dcl-color-chat-assistant-body",
  stepProgressEyebrow: "--bfrs-dcl-color-step-progress-eyebrow",
  stepProgressTitle: "--bfrs-dcl-color-step-progress-title",
  stepProgressDoneMarker: "--bfrs-dcl-color-step-progress-done-marker",
  stepProgressActiveMarker: "--bfrs-dcl-color-step-progress-active-marker",
  stepProgressDoneText: "--bfrs-dcl-color-step-progress-done-text",
  stepProgressActiveText: "--bfrs-dcl-color-step-progress-active-text",
  stepProgressPending: "--bfrs-dcl-color-step-progress-pending"
} as const;

export const agenticThemeRadiusVariables = {
  sm: "--bfrs-radius-sm",
  md: "--bfrs-radius-md",
  lg: "--bfrs-radius-lg",
  xl: "--bfrs-radius-xl",
  "2xl": "--bfrs-radius-2xl",
  pill: "--bfrs-radius-pill",
  dclSm: "--bfrs-dcl-radius-sm",
  dclMd: "--bfrs-dcl-radius-md",
  dclLg: "--bfrs-dcl-radius-lg",
  dclXl: "--bfrs-dcl-radius-xl",
  dcl2xl: "--bfrs-dcl-radius-2xl",
  control: "--bfrs-radius-control",
  chip: "--bfrs-radius-chip",
  badge: "--bfrs-radius-badge",
  menu: "--bfrs-radius-menu",
  modal: "--bfrs-radius-modal",
  card: "--bfrs-radius-card",
  button: "--bfrs-button-radius",
  input: "--bfrs-input-radius",
  tabs: "--bfrs-tabs-radius"
} as const;

export const agenticThemeShadowVariables = {
  low: "--bfrs-shadow-low",
  card: "--bfrs-shadow-card",
  cardHover: "--bfrs-shadow-card-hover",
  elevated: "--bfrs-shadow-elevated",
  overlay: "--bfrs-shadow-overlay",
  menu: "--bfrs-shadow-menu",
  drawer: "--bfrs-shadow-drawer",
  modal: "--bfrs-shadow-modal",
  sticky: "--bfrs-shadow-sticky"
} as const;

export const agenticThemeGradientVariables = {
  card: "--bfrs-gradient-card",
  cardBorder: "--bfrs-gradient-card-border",
  cardBorderHover: "--bfrs-gradient-card-border-hover",
  cardSubtle: "--bfrs-gradient-card-subtle",
  businessInfoCard: "--bfrs-gradient-business-info-card",
  businessCardHeader: "--bfrs-gradient-business-card-header"
} as const;

export const agenticThemeSpacingVariables = {
  controlPaddingX: "--bfrs-space-control-padding-x",
  controlPaddingY: "--bfrs-space-control-padding-y",
  tableCellPadding: "--bfrs-space-table-cell-padding",
  sectionGap: "--bfrs-space-section-gap",
  panelGap: "--bfrs-space-panel-gap"
} as const;

export const agenticThemeTypographyVariables = {
  fontSans: "--bfrs-font-sans"
} as const;

export const agenticThemeMotionVariables = {
  fast: "--bfrs-motion-fast",
  normal: "--bfrs-motion-normal",
  slow: "--bfrs-motion-slow",
  easeStandard: "--bfrs-ease-standard",
  easeOut: "--bfrs-ease-out"
} as const;

export const agenticThemeTokens = {
  colors: agenticThemeColorVariables,
  radius: agenticThemeRadiusVariables,
  shadows: agenticThemeShadowVariables,
  gradients: agenticThemeGradientVariables,
  spacing: agenticThemeSpacingVariables,
  typography: agenticThemeTypographyVariables,
  motion: agenticThemeMotionVariables
} as const;

export type AgenticThemeColorToken = keyof typeof agenticThemeColorVariables;
export type AgenticThemeRadiusToken = keyof typeof agenticThemeRadiusVariables;
export type AgenticThemeShadowToken = keyof typeof agenticThemeShadowVariables;
export type AgenticThemeGradientToken = keyof typeof agenticThemeGradientVariables;
export type AgenticThemeSpacingToken = keyof typeof agenticThemeSpacingVariables;
export type AgenticThemeTypographyToken = keyof typeof agenticThemeTypographyVariables;
export type AgenticThemeMotionToken = keyof typeof agenticThemeMotionVariables;

export type AgenticThemeOverrides = {
  colors?: Partial<Record<AgenticThemeColorToken, ThemeTokenValue>>;
  radius?: Partial<Record<AgenticThemeRadiusToken, ThemeTokenValue>>;
  shadows?: Partial<Record<AgenticThemeShadowToken, ThemeTokenValue>>;
  gradients?: Partial<Record<AgenticThemeGradientToken, ThemeTokenValue>>;
  spacing?: Partial<Record<AgenticThemeSpacingToken, ThemeTokenValue>>;
  typography?: Partial<Record<AgenticThemeTypographyToken, ThemeTokenValue>>;
  motion?: Partial<Record<AgenticThemeMotionToken, ThemeTokenValue>>;
  variables?: Partial<Record<ThemeVariableName | string, ThemeTokenValue>>;
};

export type AgenticThemeVariables = Partial<Record<ThemeVariableName, string>>;

const hslColorTokens = new Set<AgenticThemeColorToken>([
  "primary",
  "primaryBright",
  "primaryHover",
  "primaryActive",
  "primarySoft",
  "primaryBorder",
  "primaryContrast",
  "accent",
  "accentSoft",
  "surfaceApp",
  "surfaceShell",
  "surfaceCard",
  "surfaceMuted",
  "surfaceRaised",
  "textPrimary",
  "textSecondary",
  "textMuted",
  "textDisabled",
  "textInverse",
  "border",
  "borderAlias",
  "borderLight",
  "borderStrong",
  "borderFocus",
  "formControlBorder",
  "success",
  "successSoft",
  "successContrast",
  "danger",
  "dangerSoft",
  "dangerContrast",
  "warning",
  "warningSoft",
  "warningContrast",
  "info",
  "infoSoft",
  "infoContrast",
  "agentOrders",
  "agentNdr",
  "agentRto",
  "agentTracking",
  "agentInsights"
]);

const normalizeVariableValue = (value: ThemeTokenValue) => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : undefined;
};

const normalizeVariableName = (name: string): ThemeVariableName => {
  const normalized = name.startsWith("--") ? name.slice(2) : name;
  return `--${normalized.startsWith("bfrs-") ? normalized : `bfrs-${normalized}`}` as ThemeVariableName;
};

const round = (value: number) => Number(value.toFixed(2)).toString();

const rgbToHslChannels = (red: number, green: number, blue: number, alpha?: number) => {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;

  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === r) hue = (g - b) / delta + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue *= 60;
  }

  const channels = `${round(hue)} ${round(saturation * 100)}% ${round(lightness * 100)}%`;
  return alpha === undefined ? channels : `${channels} / ${round(alpha)}`;
};

const normalizeHslSyntax = (channels: string) => {
  const trimmed = channels.trim();

  if (trimmed.includes(",")) {
    const parts = trimmed.split(",").map((part) => part.trim());
    const alpha = parts[3] ? ` / ${parts[3]}` : "";
    return `${parts[0]} ${parts[1]} ${parts[2]}${alpha}`.trim();
  }

  return trimmed.replace(/\s+/g, " ");
};

const normalizeHexToHslChannels = (value: string) => {
  const hex = value.slice(1);
  if (![3, 4, 6, 8].includes(hex.length)) {
    return undefined;
  }

  const expanded = hex.length <= 4 ? hex.split("").map((character) => character + character).join("") : hex;
  const red = Number.parseInt(expanded.slice(0, 2), 16);
  const green = Number.parseInt(expanded.slice(2, 4), 16);
  const blue = Number.parseInt(expanded.slice(4, 6), 16);
  const alpha = expanded.length === 8 ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : undefined;

  if ([red, green, blue].some((channel) => Number.isNaN(channel))) {
    return undefined;
  }

  return rgbToHslChannels(red, green, blue, alpha);
};

const normalizeRgbToHslChannels = (value: string) => {
  const match = value.match(/^rgba?\(\s*([.\d]+)\s*,\s*([.\d]+)\s*,\s*([.\d]+)(?:\s*,\s*([.\d]+%?))?\s*\)$/i);
  if (!match) {
    return undefined;
  }

  const red = Number(match[1]);
  const green = Number(match[2]);
  const blue = Number(match[3]);
  const alpha = match[4]?.endsWith("%") ? Number(match[4].slice(0, -1)) / 100 : match[4] ? Number(match[4]) : undefined;

  if ([red, green, blue, alpha ?? 1].some((channel) => Number.isNaN(channel))) {
    return undefined;
  }

  return rgbToHslChannels(red, green, blue, alpha);
};

export function normalizeThemeColorValue(value: string) {
  const trimmed = value.trim();
  const hslMatch = trimmed.match(/^hsla?\((.*)\)$/i);
  if (hslMatch) {
    return normalizeHslSyntax(hslMatch[1]);
  }

  if (trimmed.startsWith("#")) {
    return normalizeHexToHslChannels(trimmed) ?? trimmed;
  }

  if (/^rgba?\(/i.test(trimmed)) {
    return normalizeRgbToHslChannels(trimmed) ?? trimmed;
  }

  return normalizeHslSyntax(trimmed);
}

const assignGroupVariables = <Token extends string>(
  output: AgenticThemeVariables,
  values: Partial<Record<Token, ThemeTokenValue>> | undefined,
  variables: Record<Token, ThemeVariableName>,
  normalize?: (token: Token, value: string) => string
) => {
  if (!values) {
    return;
  }

  for (const [token, value] of Object.entries(values) as [Token, ThemeTokenValue][]) {
    const normalizedValue = normalizeVariableValue(value);
    if (normalizedValue === undefined) {
      continue;
    }

    output[variables[token]] = normalize ? normalize(token, normalizedValue) : normalizedValue;
  }
};

const assignCustomVariables = (output: AgenticThemeVariables, values: AgenticThemeOverrides["variables"]) => {
  if (!values) {
    return;
  }

  for (const [name, value] of Object.entries(values)) {
    const normalizedValue = normalizeVariableValue(value);
    if (normalizedValue === undefined) {
      continue;
    }

    output[normalizeVariableName(name)] = normalizedValue;
  }
};

export function createAgenticThemeVariables(theme: AgenticThemeOverrides): AgenticThemeVariables {
  const output: AgenticThemeVariables = {};

  assignGroupVariables(output, theme.colors, agenticThemeColorVariables, (token, value) =>
    hslColorTokens.has(token) ? normalizeThemeColorValue(value) : value
  );
  assignGroupVariables(output, theme.radius, agenticThemeRadiusVariables);
  assignGroupVariables(output, theme.shadows, agenticThemeShadowVariables);
  assignGroupVariables(output, theme.gradients, agenticThemeGradientVariables);
  assignGroupVariables(output, theme.spacing, agenticThemeSpacingVariables);
  assignGroupVariables(output, theme.typography, agenticThemeTypographyVariables);
  assignGroupVariables(output, theme.motion, agenticThemeMotionVariables);
  assignCustomVariables(output, theme.variables);

  return output;
}

export function createAgenticThemeCssText(theme: AgenticThemeOverrides) {
  return Object.entries(createAgenticThemeVariables(theme))
    .map(([name, value]) => `${name}: ${value};`)
    .join("\n");
}

const agenticThemeScopeSelector = ".bfrs, [data-bfrs-root], [data-bfrs-mount]";

const hasThemeStyle = (target: ThemeTarget | undefined): target is { style: ThemeStyle } => Boolean(target && "style" in target && target.style);

const resolveThemeStyle = (target?: ThemeTarget) => {
  if (hasThemeStyle(target)) {
    return target.style;
  }

  const resolvedTarget = target ?? (typeof document === "undefined" ? undefined : document);
  const scopedTarget = resolvedTarget?.querySelector?.(agenticThemeScopeSelector);
  return scopedTarget instanceof HTMLElement ? scopedTarget.style : undefined;
};

export function applyAgenticTheme(theme: AgenticThemeOverrides, target?: ThemeTarget) {
  const variables = createAgenticThemeVariables(theme);
  const style = resolveThemeStyle(target);

  if (style) {
    for (const [name, value] of Object.entries(variables)) {
      if (value !== undefined) {
        style.setProperty(name, value);
      }
    }
  }

  return variables;
}

export function clearAgenticTheme(variableNames: Iterable<string>, target?: ThemeTarget) {
  const style = resolveThemeStyle(target);

  if (style) {
    for (const name of variableNames) {
      style.removeProperty(normalizeVariableName(name));
    }
  }
}
